/**
 * Return navigation for public marketing pages (teacher, location).
 *
 * Priority:
 * 1. `?fromClass=<numericClassId>` - shareable class context (highest)
 * 2. `?fromTeacher=<numericTeacherId>` - specific instructor profile
 * 3. `?from=teachers` or `?from=locations` - listing page context
 * 3. Legacy sessionStorage (class id only)
 * 4. `document.referrer` - `/classes/...`, `/teachers/...`, `/teachers`, or `/locations`
 * 5. Default → classes listing
 */

import { PageTypeGlobal } from "@/lib/types";
import { parsePublicSlugId } from "@/lib/utils/publicSlugs";
import {
  PUBLIC_LOCATION_RETURN_CLASS_ID,
  PUBLIC_TEACHER_RETURN_CLASS_ID,
} from "@/lib/utils/publicPaths";

/** Query param name - numeric class id (matches `GetClassResponse.classId`). */
export const PUBLIC_FROM_CLASS_QUERY = "fromClass";

/** Query param - numeric teacher id (matches `GetTeacherResponse.teacherId`). */
export const PUBLIC_FROM_TEACHER_QUERY = "fromTeacher";

/** Query param - listing page the user navigated from. */
export const PUBLIC_FROM_QUERY = "from";

export type PublicReturnListing = "teachers" | "locations";

export type PublicDetailPage = "teacher" | "location";

export interface PublicBackLink {
  href: string;
  label: string;
}

export function parseFromClassQueryValue(raw: string | null): number | null {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}

export function parseFromListingQueryValue(
  raw: string | null,
): PublicReturnListing | null {
  if (raw === "teachers" || raw === "locations") return raw;
  return null;
}

export function parseFromTeacherQueryValue(raw: string | null): number | null {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}

/** Append `fromClass` to a path for public links from the class page. */
export function appendFromClassQuery(href: string, classId: number): string {
  if (classId <= 0) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}${PUBLIC_FROM_CLASS_QUERY}=${encodeURIComponent(String(classId))}`;
}

export function appendFromTeacherQuery(href: string, teacherId: number): string {
  if (teacherId <= 0) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}${PUBLIC_FROM_TEACHER_QUERY}=${encodeURIComponent(String(teacherId))}`;
}

export function appendFromListingQuery(
  href: string,
  from: PublicReturnListing,
): string {
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}${PUBLIC_FROM_QUERY}=${encodeURIComponent(from)}`;
}

export function appendPublicReturnQuery(
  href: string,
  context: {
    classId?: number | null;
    teacherId?: number | null;
    fromListing?: PublicReturnListing | null;
  },
): string {
  if (context.classId != null && context.classId > 0) {
    return appendFromClassQuery(href, context.classId);
  }
  if (context.teacherId != null && context.teacherId > 0) {
    return appendFromTeacherQuery(href, context.teacherId);
  }
  if (context.fromListing) {
    return appendFromListingQuery(href, context.fromListing);
  }
  return href;
}

export function getDefaultPublicBackLink(): PublicBackLink {
  return { href: PageTypeGlobal.Classes, label: "Back to classes" };
}

export function getTeachersListingBackLink(): PublicBackLink {
  return { href: PageTypeGlobal.Teachers, label: "Back to instructors" };
}

export function getLocationsListingBackLink(): PublicBackLink {
  return { href: PageTypeGlobal.Locations, label: "Back to locations" };
}

export function getPublicDetailPageDefaultBackLink(
  page: PublicDetailPage,
): PublicBackLink {
  return page === "teacher"
    ? getTeachersListingBackLink()
    : getLocationsListingBackLink();
}

/**
 * If `document.referrer` is a `/classes/...` URL on this origin, parse class id from the slug.
 */
export function parseClassIdFromClassesReferrer(): number | null {
  if (typeof document === "undefined") return null;
  try {
    const ref = document.referrer;
    if (!ref) return null;
    const u = new URL(ref);
    if (!u.pathname.startsWith("/classes/")) return null;
    const seg = u.pathname.replace(/\/$/, "").split("/").pop() ?? "";
    const id = Number(seg.split("-").pop());
    if (Number.isNaN(id) || id <= 0) return null;
    return id;
  } catch {
    return null;
  }
}

/** Referrer is a public teacher detail page (`/teachers/<slug>`). */
export function parseTeacherIdFromTeachersReferrer(): number | null {
  if (typeof document === "undefined") return null;
  try {
    const ref = document.referrer;
    if (!ref) return null;
    const u = new URL(ref);
    if (u.origin !== window.location.origin) return null;
    if (!u.pathname.startsWith(`${PageTypeGlobal.Teachers}/`)) return null;
    const slug = u.pathname.replace(/\/$/, "").split("/").pop() ?? "";
    return parsePublicSlugId(slug);
  } catch {
    return null;
  }
}

/** Referrer is exactly the public teachers or locations listing (not a detail slug). */
export function parseListingFromReferrer(): PublicReturnListing | null {
  if (typeof document === "undefined") return null;
  try {
    const ref = document.referrer;
    if (!ref) return null;
    const u = new URL(ref);
    if (u.origin !== window.location.origin) return null;
    const path = u.pathname.replace(/\/$/, "") || "/";
    if (path === PageTypeGlobal.Teachers) return "teachers";
    if (path === PageTypeGlobal.Locations) return "locations";
    return null;
  } catch {
    return null;
  }
}

/** Read legacy session keys (same value: originating class id). */
export function consumeReturnClassIdFromSessionStorage(): number | null {
  try {
    const a = sessionStorage.getItem(PUBLIC_TEACHER_RETURN_CLASS_ID);
    const b = sessionStorage.getItem(PUBLIC_LOCATION_RETURN_CLASS_ID);
    const raw = a ?? b ?? "";
    sessionStorage.removeItem(PUBLIC_TEACHER_RETURN_CLASS_ID);
    sessionStorage.removeItem(PUBLIC_LOCATION_RETURN_CLASS_ID);
    if (raw === "") return null;
    const n = Number(raw);
    if (Number.isNaN(n) || n <= 0) return null;
    return n;
  } catch {
    return null;
  }
}
